import React, { createContext, useState, useContext, useCallback } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { getApiBaseUrl } from '../config';

/**
 * Image library of the shared image-picker (DJB-231 / DJB-233).
 *
 * Images live in pools that each map to one S3 folder on the server:
 *   - `ads` → advertenties/ (ad + screensaver images, uploaded via
 *                            POST /api/storage/uploadAdvertentie)
 *
 * Managed player backgrounds are their own entity (BackgroundContext) and
 * playlist backgrounds are a plain upload to playlistbackgrounds/
 * (UploadContext.uploadPlaylistBackgroundImage); neither lives in an
 * image-library pool.
 *
 * Uploading stays in UploadContext; this context only lists a pool. Images
 * are never deleted from the admin — they are removed together with the
 * concept that owns them.
 *
 * @typedef {Object} ImageLibraryEntry
 * @property {string} pool       - 'ads'
 * @property {string} fileName   - S3 file name; the value stored in imageRef / displayLogoRef
 * @property {string} url        - public URL of the image
 */

export const IMAGE_POOLS = Object.freeze({
  ADS: 'ads',
});

/** Normalises a server entry (or a bare file name from older list endpoints). */
export const normalizeImageEntry = (entry, pool) => {
  if (entry == null) return null;
  if (typeof entry === 'string') {
    return { pool, fileName: entry, url: null };
  }
  const fileName = entry.fileName ?? entry.name ?? '';
  if (!fileName) return null;
  return {
    pool: entry.pool ?? pool,
    fileName,
    url: entry.url ?? null,
  };
};

export const ImageLibraryContext = createContext({
  /** @type {(pool: string) => Promise<ImageLibraryEntry[]>} */
  fetchImageLibrary: async () => [],
});

const ImageLibraryProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now() + Math.random();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };
  const showErrorNotification = (message) => addNotification(message, 'error');

  const accessToken = authData?.accessToken;

  /**
   * GET /image-library/{pool} – every image stored in the pool. Returns []
   * (after a notification) when the request fails so callers can always
   * render.
   * @param {'ads'} pool
   * @returns {Promise<ImageLibraryEntry[]>}
   */
  const fetchImageLibrary = useCallback(async (pool) => {
    if (!accessToken || !pool) return [];
    try {
      const response = await fetch(`${getApiBaseUrl()}/image-library/${encodeURIComponent(pool)}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();
      return Array.isArray(data)
        ? data.map((entry) => normalizeImageEntry(entry, pool)).filter(Boolean)
        : [];
    } catch (err) {
      showErrorNotification(err.message ?? 'Failed to fetch images');
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <ImageLibraryContext.Provider value={{ fetchImageLibrary }}>
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() =>
              setNotifications((prev) => prev.filter((n) => n.id !== notification.id))
            }
          />
        ))}
      </div>
    </ImageLibraryContext.Provider>
  );
};

export default ImageLibraryProvider;
